import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  ArrowDropUp,
  AttachMoney,
  InfoOutlined,
  InsertDriveFileOutlined,
  Article,
  RecordVoiceOver,
} from '@mui/icons-material'
import { connect, ErrorMessage, FieldArray, FormikContextType } from 'formik'
import { inject } from 'mobx-react'
import moment from 'moment'
import React from 'react'
import FullWidthField from '../common/FullWidthField'
import SelectField from '../common/SelectField/SelectField'
import TitleBar from '../common/TitleBar/TitleBar'
import { ICMS1500, ILocation, IProvider, ISchedule } from '../Definitions'
import {
  AddressDto,
  EpisodeOfCareDto,
  IEpisodeOfCareDto,
  IProviderDto,
  IProviderTypeDto,
  ITypeDto,
} from '../generated_client'
import { IStores } from '../Stores'
import Box24 from './Box24'
import { Diagnosis, IClaimFormValues, createIClaimFormValues } from './ClaimFormValues'
import { ILineItemFormValues } from './LineItemFormValues'
import './ClaimForm.css'
import AppealNameConstants from 'src/constants/AppealNameConstants'
import ClaimDocumentButton from './ClaimDocumentButton'
import GeneratePDFDocumentButton from './GeneratePDFDocumentButton'
import QuoteTypeNameConstants from 'src/constants/QuoteTypeNameConstants'
import ReferralContactModal from 'src/patients/EpisodesOfCare/ReferralContactModal'

interface IBasicFormProps {
  episodeOfCare?: IEpisodeOfCareDto
  getTypesByCategory?: (type: string) => void
  isCurrentOrganizationSurgical?: boolean
  loadClaim?: (claimId: string) => Promise<void>
  locations?: ILocation[]
  openProviderLookupDialog?: () => void
  resetSelectedQuote?: () => void
  selectedCMS1500?: ICMS1500
  selectedLocation?: ILocation
  selectedProvider?: IProvider
  selectedSchedule?: ISchedule
  setSelectedCMS1500?: (cms1500: ICMS1500 | undefined) => void
  setSelectedLocation?: (location?: ILocation) => void
  setSelectedProvider?: (provider?: IProvider) => void
  tcTypes?: ITypeDto[]
  loadBodyParts?: () => Promise<void>
  errorState?: boolean
  dueDateBefore?: boolean
  showContractModal?: (provider: IProviderDto | undefined) => void
  dueDate?: string | undefined
  handleDueDateChange?: (dueDateVal: string) => void
  receivedDate?: string | undefined
  surgeryDate?: Date | undefined
  handleDateChange?: (event: any) => void
  openContractModal?: (index: number) => void
  showContractModalButton: boolean
  showPDFButton: boolean
  hasNotSubmitted: boolean
  isReferralContactModalOpen?: boolean
  openReferralContactModal?: () => void
}

interface IFormikProps {
  formik: FormikContextType<IClaimFormValues>
}

class BasicForm extends React.Component<IBasicFormProps & IFormikProps> {
  constructor(props: IBasicFormProps & IFormikProps) {
    super(props)
  }

  public state = {
    isLoading: false,
  }

  public initialValues = createIClaimFormValues(
    this.props.selectedCMS1500,
    new EpisodeOfCareDto(this.props.episodeOfCare)
  )

  public getLinkPath() {
    return (
      '/patients/patient/' +
      this.props.episodeOfCare?.referral?.patientId +
      '/referral/' +
      this.props.episodeOfCare?.referralId +
      '/episodeOfCare/' +
      this.props.episodeOfCare?.id
    )
  }

  public render() {
    const { formik, showContractModal } = this.props
    const isFullyReconciled =
      (this.props.selectedCMS1500?.percentageOfLineItemsReconciled ?? 0) >= 100
    const disableDollarAmounts =
      isFullyReconciled ||
      [
        'Denied',
        'Paid',
        'Paid-Holding',
        'Imported Paid',
        'Imported Denied',
        'Deleted',
        'Imported',
        'Refunded',
      ].includes(this.props.selectedCMS1500?.billStatus ?? '')
    const reconciliationLabel = this.props.selectedCMS1500?.inGlobal ? (
      <span style={{ fontSize: '16px' }}>
        {' '}
        (Claim Reconciliation:{' '}
        {this.props.selectedCMS1500.percentageOfLineItemsReconciled ?? 0}%)
      </span>
    ) : undefined

    if (this.props.selectedSchedule && this.props.selectedSchedule!.appointmentDate) {
      this.initialValues.dateOfService = formik.values.dateOfService
      this.initialValues.q14DateOfIllness = formik.values.q14DateOfIllness
    } else {
      this.initialValues.q14DateOfIllness = formik.values.q14DateOfIllness
    }

    if (this.props.selectedSchedule && this.props.selectedSchedule!.appointmentDate) {
      formik.values.dateOfService = this.props.selectedSchedule!.appointmentDate!
    }

    if (this.props.selectedLocation && this.props.selectedLocation!.id) {
      if (
        formik.values.providerLocationId === undefined ||
        formik.values.providerLocationId === ''
      ) {
        formik.values.providerLocationId = this.props.selectedLocation!.id
        formik.values.providerLocation = this.props.selectedLocation
      }
    }

    return (
      <>
        <Grid style={{ padding: '8px' }}>
          {this.state.isLoading && (
            <div
              style={{
                position: 'fixed',
                display: 'flex',
                width: '100%',
                height: '100%',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                cursor: 'pointer',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: '2',
              }}
            >
              <CircularProgress size={100} />
            </div>
          )}
          {this.props.isReferralContactModalOpen && (
            <ReferralContactModal
              selectedReferralId={this.props.episodeOfCare?.referralId!}
            />
          )}
          <Grid item>
            <Grid container item style={{ padding: 8 }}>
              <Accordion
                defaultExpanded
                sx={{ marginBottom: '8px', border: '1px solid', borderColor: 'divider' }}
              >
                <AccordionSummary expandIcon={<ArrowDropUp />}>
                  <TitleBar
                    title={
                      <>
                        {'General - ' +
                          (this.props.selectedCMS1500?.billStatus == 'New'
                            ? 'RECEIVED'
                            : this.props.selectedCMS1500?.billStatus
                              ? this.props.selectedCMS1500?.billStatus.toUpperCase()
                              : '[UNKNOWN]')}
                        {reconciliationLabel}
                      </>
                    }
                    titleButton={
                      this.props.selectedCMS1500?.billStatus === 'Denied' &&
                      this.props.selectedCMS1500.denialLetterDocumentUrl ? (
                        <Tooltip title="Show Latest Denial Documentation">
                          <Article
                            fontSize="small"
                            color="error"
                            aria-label="View latest denial documentation"
                            style={{ cursor: 'pointer' }}
                            onClick={(event) => {
                              event.stopPropagation()
                              window.open(
                                this.props.selectedCMS1500!.denialLetterDocumentUrl!
                              )
                            }}
                          />
                        </Tooltip>
                      ) : undefined
                    }
                    icon={AttachMoney}
                    titleComponent={
                      <div style={{ paddingTop: '4px', paddingLeft: '8px' }}>
                        {this.props.showPDFButton && (
                          <GeneratePDFDocumentButton
                            claimId={formik.values.id}
                            disabled={
                              formik.values.id == '' || this.props.hasNotSubmitted
                            }
                            setIsLoading={(loading: boolean) =>
                              this.setState({ isLoading: loading })
                            }
                          />
                        )}
                        <ClaimDocumentButton
                          docId={this.props.selectedCMS1500?.stagedDocumentId ?? ''}
                          setIsLoading={(loading: boolean) =>
                            this.setState({ isLoading: loading })
                          }
                        />
                      </div>
                    }
                    isTitleComponentPushedRight={true}
                  />
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container item spacing={1}>
                    <Grid item container xs={12} spacing={2}>
                      <Grid item>
                        <strong>Patient Name:</strong>&nbsp;
                        {this.props.episodeOfCare
                          ? this.props.episodeOfCare.referral!.patient!.firstName +
                            ' ' +
                            this.props.episodeOfCare.referral!.patient!.lastName
                          : 'undefined'}
                      </Grid>
                      <Grid
                        item
                        container
                        spacing={1}
                        xs={1}
                        style={{ minWidth: 'fit-content' }}
                      >
                        <Grid item>
                          <strong>EOC/Legacy:</strong>
                        </Grid>
                        <Grid item>
                          <Button
                            variant="text"
                            color="primary"
                            sx={{ userSelect: 'text' }}
                            style={{
                              textTransform: 'none',
                              padding: 0,
                              minWidth: 0,
                              background: 'none',
                              border: 'none',
                              boxShadow: 'none',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              fontWeight: 'normal',
                              fontSize: 'inherit',
                              lineHeight: 'inherit',
                            }}
                            onMouseOver={(e) => {
                              ;(e.currentTarget as HTMLElement).style.textDecoration =
                                'underline'
                            }}
                            onMouseOut={(e) => {
                              ;(e.currentTarget as HTMLElement).style.textDecoration =
                                'underline'
                            }}
                            onClick={() => {
                              sessionStorage.setItem('patientTab', '0')
                              sessionStorage.setItem('eocTab', '1')
                              window.open(this.getLinkPath())
                            }}
                          >
                            {this.props.episodeOfCare
                              ? this.props.episodeOfCare.episodeOfCareNumberOrLegacyId
                              : 'undefined'}
                          </Button>
                        </Grid>
                      </Grid>
                      <Grid item>
                        <Tooltip title="Show Referral Contact">
                          <RecordVoiceOver
                            fontSize="small"
                            color={'primary'}
                            style={{
                              cursor: 'pointer',
                            }}
                            onClick={this.props.openReferralContactModal}
                          />
                        </Tooltip>
                      </Grid>
                      <Grid item>
                        <strong>Group #:</strong>&nbsp;
                        {this.props.selectedCMS1500
                          ? this.props.selectedCMS1500.groupNumber
                            ? this.props.selectedCMS1500.groupNumber
                            : 'TBD'
                          : 'TBD'}
                      </Grid>
                      <Grid item>
                        <strong>Body Part:</strong>&nbsp;
                        {this.props.selectedSchedule?.bodyParts != undefined
                          ? this.props.selectedSchedule!.bodyParts
                          : this.props.episodeOfCare
                            ? this.props.episodeOfCare!.bodyPartText
                            : ''}
                      </Grid>
                      <Grid item>
                        {this.props.selectedCMS1500?.appealType != undefined ? (
                          <Typography noWrap variant="body2" color="error">
                            <strong>Appeal Type:</strong>&nbsp;
                            {AppealNameConstants[this.props.selectedCMS1500?.appealType]}
                          </Typography>
                        ) : (
                          <></>
                        )}
                      </Grid>
                    </Grid>
                    <Grid item container xs={12} spacing={2}>
                      <Grid item>
                        <strong>ToC Date:</strong>&nbsp;
                        {this.props.episodeOfCare != undefined &&
                        this.props.episodeOfCare.effectiveDate != undefined
                          ? moment(this.props.episodeOfCare.effectiveDate).format('L')
                          : 'N/A'}
                      </Grid>
                      <Grid item>
                        <strong>Bundle Type:</strong>&nbsp;
                        {this.props.episodeOfCare != undefined &&
                        this.props.episodeOfCare.quote != undefined &&
                        this.props.episodeOfCare.quote.type != undefined
                          ? QuoteTypeNameConstants[this.props.episodeOfCare.quote.type]
                          : 'N/A'}
                      </Grid>
                      <Grid item>
                        <strong>Surgery Date:</strong>&nbsp;
                        {this.props.surgeryDate != undefined
                          ? moment(this.props.surgeryDate).format('L')
                          : 'N/A'}
                      </Grid>
                      <Grid item>
                        <strong># of Days:</strong>&nbsp;
                        {this.props.episodeOfCare != undefined &&
                        this.props.episodeOfCare.quote != undefined &&
                        this.props.episodeOfCare.quote.bundleDurationDays != undefined
                          ? this.props.episodeOfCare.quote.bundleDurationDays
                          : 'N/A'}
                      </Grid>
                      <Grid item>
                        <strong>End Date:</strong>&nbsp;
                        {this.props.episodeOfCare != undefined &&
                        this.props.episodeOfCare.quote != undefined &&
                        this.props.episodeOfCare.quote.endDate != undefined
                          ? moment(this.props.episodeOfCare.quote.endDate).format('L')
                          : 'N/A'}
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <fieldset
                        disabled={isFullyReconciled}
                        style={{
                          border: 0,
                          margin: 0,
                          minWidth: 0,
                          padding: 0,
                          width: '100%',
                        }}
                      >
                        <Grid
                          container
                          spacing={2}
                          style={{ paddingBottom: '34px' }}
                          alignItems="flex-end"
                        >
                          <Grid item>
                            <strong>Date of Service:</strong>&nbsp;
                            {moment(formik.values.dateOfService).format('L')}
                          </Grid>
                          <Grid item>
                            <strong>Provider Group:</strong>&nbsp;
                            <span style={{ paddingRight: '4px' }}>
                              {this.getLocationProvider(formik.values)}
                            </span>
                            <Tooltip title="View Provider Contract Summary">
                              <InsertDriveFileOutlined
                                style={{
                                  cursor: 'pointer',
                                  fontSize: '16px',
                                }}
                                onClick={() => {
                                  showContractModal!(
                                    formik.values.providerLocation?.provider
                                  )
                                }}
                              />
                            </Tooltip>
                          </Grid>
                          <Grid item>
                            <strong>Provider Location:</strong>&nbsp;
                            {`${formik.values.providerLocation?.name} ${formik.values.providerLocation?.physicalAddress?.addressDisplay}`}
                            {formik.values.providerLocation &&
                              formik.values.q33BillingProviderInfo && (
                                <Tooltip
                                  componentsProps={{
                                    tooltip: { sx: { fontSize: '0.85rem', p: 1.5 } },
                                  }}
                                  title={
                                    <span style={{ whiteSpace: 'pre-line' }}>
                                      <strong>Billing Address:</strong>
                                      {' \n'}
                                      {formik.values.q33BillingProviderInfo}
                                    </span>
                                  }
                                >
                                  <InfoOutlined
                                    style={{
                                      fontSize: '20px',
                                      marginLeft: '4px',
                                      cursor: 'pointer',
                                      verticalAlign: 'middle',
                                    }}
                                  />
                                </Tooltip>
                              )}
                          </Grid>
                        </Grid>
                        <Grid
                          container
                          spacing={2}
                          style={{ paddingBottom: 12 }}
                          alignItems="flex-end"
                        >
                          <Grid item xs={3}>
                            <SelectField
                              size="small"
                              label="Provider Type"
                              name="providerTypeId"
                              inputId="providerTypeId"
                              getName={this.getProviderTypeName}
                              getValue={this.getProviderTypeValue}
                              items={this.getProviderTypes(formik.values)}
                              fullWidth
                              disabled={
                                isFullyReconciled ||
                                this.isProviderTypeDisabled(formik.values)
                              }
                              shrinkLabel={!!formik.values.providerTypeId}
                              outlined={false}
                              required
                            />
                          </Grid>
                          <Grid item xs={2}>
                            <TextField
                              disabled={isFullyReconciled}
                              inputProps={{
                                id: 'incomingOrOutgoingDate',
                              }}
                              InputLabelProps={{ shrink: true }}
                              onChange={this.props.handleDateChange}
                              type="date"
                              name="incomingOrOutgoingDate"
                              variant="standard"
                              value={this.props.receivedDate}
                              label="Date Received"
                              fullWidth
                              required
                              error={this.props.receivedDate == ''}
                            />
                          </Grid>
                          <Grid item xs={2}>
                            <TextField
                              disabled={isFullyReconciled}
                              inputProps={{
                                id: 'dueDate',
                              }}
                              InputLabelProps={{ shrink: true }}
                              type="date"
                              name="dueDate"
                              onChange={this.handleDueDateChange}
                              variant="standard"
                              value={this.props.dueDate}
                              label="Date Due"
                              fullWidth
                              required
                              error={this.props.dueDate == ''}
                            />
                            {this.props.dueDateBefore && (
                              <div className="error">
                                Due date must occur on or after Date Received.
                              </div>
                            )}
                          </Grid>
                          <Grid item xs={2}>
                            <FullWidthField
                              variant="standard"
                              name="q28TotalCharge"
                              label="Total Charge"
                              type="number"
                              disabled={disableDollarAmounts}
                              required
                              InputLabelProps={{
                                shrink: true,
                              }}
                            />
                            {this.props.errorState && (
                              <div className="error">
                                Sum of claim line charges must match Total Charges
                                entered.
                              </div>
                            )}
                          </Grid>
                          {this.props.selectedCMS1500?.appealType != undefined && (
                            <Grid item xs={12}>
                              <div className="red-outline">
                                <FullWidthField
                                  disabled={isFullyReconciled}
                                  name="appealReason"
                                  label="Appeal Reason"
                                  type="text"
                                  required
                                  fullWidth
                                  multiline
                                  rows="4"
                                  style={{ marginBottom: 4 }}
                                  variant="outlined"
                                />
                              </div>
                            </Grid>
                          )}
                        </Grid>
                      </fieldset>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
          <Grid item>
            <Grid container style={{ padding: 8 }}>
              <Accordion
                defaultExpanded
                sx={{ marginBottom: '8px', border: '1px solid', borderColor: 'divider' }}
              >
                <AccordionSummary expandIcon={<ArrowDropUp />}>
                  <TitleBar title={'Box 21'} icon={AttachMoney} />
                </AccordionSummary>
                <AccordionDetails>
                  <fieldset
                    disabled={isFullyReconciled}
                    style={{
                      border: 0,
                      margin: 0,
                      minWidth: 0,
                      padding: 0,
                      width: '100%',
                    }}
                  >
                    <Grid item style={{ position: 'relative' }}>
                      <p
                        style={{
                          bottom: 0,
                          margin: 0,
                          position: 'static',
                        }}
                      >
                        Diagnosis or Nature of Illness or Injury (Relate A-L to service
                        line below) *
                      </p>
                      <Grid item container direction="row" xs={12}>
                        <FieldArray
                          name="q21Diagnosis"
                          render={this.renderDiagnosisFields(
                            formik.values,
                            isFullyReconciled
                          )}
                        />
                        <ErrorMessage name="q21Diagnosis">
                          {(msg) => <div style={{ color: 'red' }}>{msg}</div>}
                        </ErrorMessage>
                      </Grid>
                    </Grid>
                  </fieldset>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
          <Grid item>
            <Grid container style={{ padding: 8 }}>
              <Accordion
                defaultExpanded
                sx={{ marginBottom: '8px', border: '1px solid', borderColor: 'divider' }}
              >
                <AccordionSummary expandIcon={<ArrowDropUp />}>
                  <TitleBar title={'Box 24'} icon={AttachMoney} />
                </AccordionSummary>
                <AccordionDetails>
                  {this.renderBox24(
                    formik.values,
                    disableDollarAmounts,
                    isFullyReconciled
                  )}
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </Grid>
      </>
    )
  }

  private openContractModal(index: number) {
    this.props.openContractModal!(index)
  }

  private renderBox24 = (
    values: IClaimFormValues,
    disableDollarAmounts: boolean,
    disabled: boolean
  ) => {
    return (
      <>
        <Grid container direction="column" spacing={1}>
          {values.q24.map((_lineItem: ILineItemFormValues, index: number) => (
            <Box24
              key={index}
              disabled={disabled}
              showContractAmount={this.props.showContractModalButton}
              disableDollarAmountEditing={disableDollarAmounts}
              disableLineItemActions={disableDollarAmounts}
              openContractModal={() => this.openContractModal(index)}
              index={index}
              providerTypeId={values.providerTypeId}
              providerId={values.providerId}
            />
          ))}
        </Grid>
      </>
    )
  }

  private renderDiagnosisFields = (values: IClaimFormValues, disabled: boolean) => () => (
    <>
      {values.q21Diagnosis.map((diagnosis: Diagnosis, index: number) => (
        <Grid item xs={3} key={index}>
          <FullWidthField
            disabled={disabled}
            name={`q21Diagnosis.${index}.code`}
            label={diagnosis.pointer}
            variant="standard"
          />
        </Grid>
      ))}
    </>
  )

  private getProviderTypes = (values: IClaimFormValues) => {
    return values.providerLocation
      ? values.providerLocation.providerTypes
        ? values.providerLocation.providerTypes!.sort((a, b) =>
            a.abbreviation!.toLowerCase().localeCompare(b.abbreviation!.toLowerCase())
          )
        : []
      : []
  }

  private getProviderTypeName = (type: IProviderTypeDto) => {
    return type.name || ''
  }

  private getProviderTypeValue = (type: IProviderTypeDto) => {
    return type.id || ''
  }

  private getLocationProvider = (values: IClaimFormValues) => {
    if (this.props.selectedLocation) {
      values.providerLocationId = this.props.selectedLocation.id
      values.providerLocation = this.props.selectedLocation
      if (this.props.selectedLocation!.provider) {
        values.provider = this.props.selectedLocation!.provider!
        values.providerId = this.props.selectedLocation!.providerId!
        values.q32ALocationNpi = this.props.selectedLocation!.npi ?? ''
        values.q32LocationInfo =
          (this.props.selectedLocation?.name
            ? this.props.selectedLocation?.name + '\n'
            : '') +
          (this.props.selectedLocation?.physicalAddress?.line1
            ? this.props.selectedLocation.physicalAddress.line1 + '\n'
            : '') +
          (this.props.selectedLocation?.physicalAddress?.line2
            ? this.props.selectedLocation.physicalAddress.line2 + '\n'
            : '') +
          (this.props.selectedLocation?.physicalAddress?.city
            ? this.props.selectedLocation.physicalAddress.city
            : '') +
          (this.props.selectedLocation?.physicalAddress?.state
            ? ', ' + this.props.selectedLocation?.physicalAddress?.state
            : '') +
          (this.props.selectedLocation?.physicalAddress?.zip
            ? ' ' + this.props.selectedLocation?.physicalAddress?.zip
            : '')
        if (
          this.props.selectedLocation &&
          this.props.selectedLocation!.billingAddresses !== undefined
        ) {
          var billingAddress = new AddressDto()
          for (var i = 0; i < this.props.selectedLocation!.billingAddresses.length; i++) {
            var address = this.props.selectedLocation!.billingAddresses[i]
            if (address.isActive && address.address != undefined) {
              billingAddress = address.address
              break
            }
          }
          values.q33BillingProviderInfo =
            (billingAddress?.name ? billingAddress?.name + '\n' : '') +
            (billingAddress?.line1 ? billingAddress.line1 + '\n' : '') +
            (billingAddress?.line2 ? billingAddress.line2 + '\n' : '') +
            (billingAddress?.city ? billingAddress.city : '') +
            (billingAddress?.state ? ', ' + billingAddress?.state : '') +
            (billingAddress?.zip ? ' ' + billingAddress?.zip : '')
        }
        values.q33ABillingProviderNpi =
          this.props.selectedLocation!.npi! !== ''
            ? this.props.selectedLocation!.npi!
            : this.props.selectedLocation!.provider?.npiNumber! !== ''
              ? this.props.selectedLocation!.provider?.npiNumber!
              : ''
        return this.props.selectedLocation?.provider?.name ?? '{UNKNOWN PROVIDER GROUP}'
      }
      return ''
    }
    return values.providerLocation?.provider?.name ?? '{UNKNOWN PROVIDER GROUP}'
  }

  private isProviderTypeDisabled = (values: IClaimFormValues) => {
    if (!!values.provider) {
      return false
    }
    return true
  }

  private handleDueDateChange = (event: any) => {
    this.props.handleDueDateChange!(event.target.value)
  }
}

const InjectedBasicForm = inject<IStores, IBasicFormProps, Partial<IBasicFormProps>, any>(
  (stores: IStores) => ({
    isCurrentOrganizationSurgical: stores.global.isCurrentOrganizationSurgical,
    loadClaim: stores.cms1500.loadCMS1500,
    locations: stores.locations.locations,
    openProviderLookupDialog: stores.schedules.openProviderLookupDialog,
    selectedCMS1500: stores.cms1500.selectedCMS1500,
    selectedLocation: stores.locations.selectedEditClaimLocation,
    selectedProvider: stores.providers.selectedProvider,
    selectedSchedule: stores.schedules.selectedSchedule,
    setSelectedCMS1500: stores.cms1500.setSelectedCMS1500,
    setSelectedLocation: stores.locations.setSelectedEditClaimLocation,
    setSelectedProvider: stores.providers.setSelectedEditClaimProvider,
    openContractModal: stores.cms1500.openContractModal,
    openReferralContactModal: stores.patientEdit.openReferralContactModal,
    isReferralContactModalOpen: stores.patientEdit.isReferralContactModalOpen,
  })
)(BasicForm)

export default connect<IBasicFormProps, IClaimFormValues>(InjectedBasicForm)
